import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Form, Button, Badge, Spinner } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { toast } from 'react-toastify';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import DataTableSkeleton from '../../components/DataTableSkeleton';
import AdminEmptyState from '../../components/admin/AdminEmptyState';
import { dataTableCustomStyles, dataTablePaginationOptions } from '../../components/admin/dataTableConfig';
import {
  listAdminInvoices,
  exportAdminInvoicesCsv,
  downloadInvoiceByNumber,
  downloadInvoiceByOrderId,
  normalizeInvoiceList,
  normalizeInvoicePagination,
  getInvoiceBuyerName,
  getGstBreakupDisplay,
} from '../../helper/invoice_helper';
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';

function Invoices() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listAdminInvoices({
        page,
        limit,
        from: from || undefined,
        to: to || undefined,
        search: search || undefined,
      });
      const list = normalizeInvoiceList(res);
      const pagination = normalizeInvoicePagination(res);

      if (res?.success === false && !list.length) {
        toast.error(res?.message || 'Failed to load invoices');
        setRows([]);
        setTotalRows(0);
        return;
      }

      setRows(list);
      setTotalRows(pagination?.total ?? list.length);
    } catch (error) {
      console.error('Invoice list error:', error);
      toast.error(error?.message || 'Failed to load invoices');
      setRows([]);
      setTotalRows(0);
    } finally {
      setLoading(false);
    }
  }, [page, limit, from, to, search]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAdminInvoicesCsv({ from, to });
      toast.success('Invoice CSV exported');
    } catch (error) {
      toast.error(error?.message || 'CSV export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDownload = async (row) => {
    const key = row._id || row.invoiceNumber;
    setDownloadingKey(key);
    try {
      if (row.invoiceNumber) {
        await downloadInvoiceByNumber(row.invoiceNumber);
      } else if (row._id) {
        await downloadInvoiceByOrderId(row._id, { invoiceNumber: row.invoiceNumber });
      } else {
        throw new Error('Missing invoice reference');
      }
      toast.success('Invoice PDF downloaded');
    } catch (error) {
      toast.error(error?.message || 'PDF download failed');
    } finally {
      setDownloadingKey(null);
    }
  };

  const columns = [
    {
      name: 'Invoice #',
      selector: (row) => row.invoiceNumber,
      sortable: true,
      minWidth: '180px',
      cell: (row) => (
        <div>
          <div className="fw-semibold small">{row.invoiceNumber || '—'}</div>
          {row.invoiceSequence != null && (
            <small className="text-muted">Seq {row.invoiceSequence}</small>
          )}
        </div>
      ),
    },
    {
      name: 'Date',
      selector: (row) => row.invoiceDate,
      sortable: true,
      width: '130px',
      cell: (row) =>
        row.invoiceDate ? moment(row.invoiceDate).format('DD MMM YYYY') : '—',
    },
    {
      name: 'Order #',
      selector: (row) => row.orderNumber,
      sortable: true,
      width: '140px',
      cell: (row) => (
        <Link to="/order" className="small fw-semibold text-decoration-none">
          {row.orderNumber || '—'}
        </Link>
      ),
    },
    {
      name: 'Buyer',
      selector: (row) => getInvoiceBuyerName(row),
      minWidth: '160px',
      cell: (row) => <span className="small">{getInvoiceBuyerName(row)}</span>,
    },
    {
      name: 'Supply',
      width: '100px',
      cell: (row) => {
        const { supplyType } = getGstBreakupDisplay(row);
        return <Badge bg="light" text="dark">{supplyType}</Badge>;
      },
    },
    {
      name: 'Taxable',
      width: '110px',
      right: true,
      cell: (row) => getGstBreakupDisplay(row).taxable,
    },
    {
      name: 'CGST',
      width: '90px',
      right: true,
      cell: (row) => getGstBreakupDisplay(row).cgst,
    },
    {
      name: 'SGST',
      width: '90px',
      right: true,
      cell: (row) => getGstBreakupDisplay(row).sgst,
    },
    {
      name: 'IGST',
      width: '90px',
      right: true,
      cell: (row) => getGstBreakupDisplay(row).igst,
    },
    {
      name: 'Grand total',
      width: '120px',
      right: true,
      cell: (row) => (
        <strong>{getGstBreakupDisplay(row).grand}</strong>
      ),
    },
    {
      name: 'PDF',
      width: '110px',
      ignoreRowClick: true,
      button: true,
      cell: (row) => {
        const key = row._id || row.invoiceNumber;
        const busy = downloadingKey === key;
        return (
          <AdminActionButton
            icon={busy ? 'ri-loader-4-line admin-action__spin' : 'ri-download-2-line'}
            label="Download invoice PDF"
            disabled={busy}
            onClick={() => handleDownload(row)}
          >
            PDF
          </AdminActionButton>
        );
      },
    },
  ];

  return (
    <>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item"><Link to="/admin/dashboard">Dashboard</Link></li>
              <li className="breadcrumb-item active" aria-current="page">GST Invoices</li>
            </ol>
            <h4 className="main-title mb-0 admin-page-title">GST Tax Invoices</h4>
            <p className="text-secondary small mb-0 mt-1">
              Serial-wise invoices for tax filing. Export CSV for your CA.
            </p>
          </div>
          <Button
            variant="primary"
            disabled={exporting}
            onClick={handleExport}
          >
            {exporting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Exporting…
              </>
            ) : (
              <>
                <i className="ri-file-excel-2-line me-1" />
                Export CSV
              </>
            )}
          </Button>
        </div>

        <Card className="card-one mb-3">
          <Card.Body>
            <Form
              className="row g-2 align-items-end"
              onSubmit={(e) => {
                e.preventDefault();
                setPage(1);
                setSearch(searchInput.trim());
              }}
            >
              <div className="col-md-3">
                <Form.Label className="small text-muted mb-1">From</Form.Label>
                <Form.Control
                  type="date"
                  value={from}
                  onChange={(e) => {
                    setFrom(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-md-3">
                <Form.Label className="small text-muted mb-1">To</Form.Label>
                <Form.Control
                  type="date"
                  value={to}
                  onChange={(e) => {
                    setTo(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              <div className="col-md-4">
                <Form.Label className="small text-muted mb-1">Search</Form.Label>
                <Form.Control
                  type="search"
                  placeholder="Invoice #, order #, buyer…"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
              </div>
              <div className="col-md-2 d-flex gap-2">
                <Button type="submit" variant="outline-primary" className="w-100">
                  Apply
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>

        <Card className="card-one">
          <Card.Body className="p-0">
            <DataTable
              columns={columns}
              data={rows}
              progressPending={loading}
              progressComponent={<DataTableSkeleton rows={8} columns={8} />}
              pagination
              paginationServer
              paginationTotalRows={totalRows}
              paginationPerPage={limit}
              paginationDefaultPage={page}
              onChangePage={(p) => setPage(p)}
              paginationComponentOptions={dataTablePaginationOptions}
              customStyles={dataTableCustomStyles}
              noDataComponent={
                <AdminEmptyState
                  title="No invoices yet"
                  description="Issued invoices appear here after payment success or delivery. You can also issue from Orders."
                />
              }
              highlightOnHover
              responsive
            />
          </Card.Body>
        </Card>

        <p className="text-muted small mt-2 mb-0">
          Totals use the GST breakup locked when the invoice was issued. PDF signature is optional.
        </p>

        <Footer />
      </div>
    </>
  );
}

export default Invoices;
